import type { PropsWithChildren } from "react";

import { cn, getIsActivePath } from "@stacklet/ui";
import { Link, useLocation } from "react-router";

import { useSettingsBreadcrumbs } from "app/hooks/routing/useSettingsBreadcrumbs";

import type { BreadCrumbElement } from "app/components/BreadCrumbsHeader";

/**
 * Vertical tab rail listing the consolidated settings views. The tab list is
 * sourced from `useSettingsBreadcrumbs` so the rail stays in sync with the
 * breadcrumb header and inherits its feature-flag gating.
 */
type SettingsTab = NonNullable<BreadCrumbElement["tabs"]>[number];

/** Group tabs by their `section` heading, preserving first-appearance order. */
function groupTabsBySection(
  tabs: SettingsTab[],
): { section?: string; tabs: SettingsTab[] }[] {
  const groups: { section?: string; tabs: SettingsTab[] }[] = [];

  for (const tab of tabs) {
    const group = groups.find((g) => g.section === tab.section);
    if (group) {
      group.tabs.push(tab);
    } else {
      groups.push({ section: tab.section, tabs: [tab] });
    }
  }

  return groups;
}

function SettingsTabRail() {
  const { pathname } = useLocation();
  const breadcrumbs = useSettingsBreadcrumbs() as BreadCrumbElement[];
  const tabs = breadcrumbs.find((crumb) => crumb.tabs)?.tabs ?? [];
  const sections = groupTabsBySection(tabs);

  return (
    <nav
      aria-label="Settings sections"
      className="flex shrink-0 flex-col gap-lg border-r border-outline-decorative pr-md"
    >
      {sections.map((group) => (
        <div className="flex flex-col gap-xs" key={group.section ?? "default"}>
          {group.section ? (
            <div className="px-xs text-right text-label-extra-small-strong capitalize text-text-secondary">
              {group.section}
            </div>
          ) : null}
          {group.tabs.map((tab) => {
            const isActive = getIsActivePath(pathname, tab.target);

            return (
              <Link
                aria-current={isActive ? "page" : undefined}
                className={cn(
                  "border-r-2 border-transparent px-xs py-xxs text-right text-label-medium-strong text-text-primary no-underline outline-none transition-colors hover:text-text-brand",
                  isActive ? "border-brand-primary text-text-brand" : undefined,
                )}
                key={tab.target}
                to={tab.target}
              >
                {tab.label}
              </Link>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

/**
 * Layout wrapper for a top-level settings view. Renders the vertical tab rail and
 * the page content together.
 */
export function SettingsTabsLayout({ children }: PropsWithChildren) {
  return (
    <div className="flex gap-2xl">
      <SettingsTabRail />
      <div className="grow">{children}</div>
    </div>
  );
}
